import { useEffect, useRef, useState } from "react";
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";
import { Loader2, Upload, Zap, X, FileText, GripVertical, ChevronUp, ChevronDown, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import NameCaseButtons from "./NameCaseButtons";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { PDF_CONVERTIBLE_RE } from "./ConvertPdfDialog";

const fmtSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const extOf = (n) => { const m = /\.[^.]+$/.exec(n || ""); return m ? m[0] : ""; };
const baseOf = (n) => (n || "").replace(/\.[^.]+$/, "");
const isPdf = (f) => f.type === "application/pdf" || /\.pdf$/i.test(f.name);
const isImg = (f) => /^image\//i.test(f.type);

const FileThumb = ({ file }) => {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let active = true, url = "";
    if (isImg(file)) { url = URL.createObjectURL(file); setSrc(url); }
    else if (isPdf(file)) {
      (async () => {
        try {
          const doc = await pdfjsLib.getDocument({ data: new Uint8Array(await file.arrayBuffer()) }).promise;
          const page = await doc.getPage(1);
          const vp = page.getViewport({ scale: 1 });
          const scale = 80 / Math.max(vp.width, vp.height);
          const v = page.getViewport({ scale });
          const c = document.createElement("canvas");
          c.width = Math.ceil(v.width); c.height = Math.ceil(v.height);
          await page.render({ canvasContext: c.getContext("2d"), viewport: v }).promise;
          if (active) setSrc(c.toDataURL("image/jpeg", 0.8));
          doc.destroy();
        } catch (e) { console.warn("Anteprima PDF non disponibile", e?.message); }
      })();
    }
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [file]);
  return (
    <div className="h-10 w-10 shrink-0 rounded border border-slate-700 bg-white overflow-hidden flex items-center justify-center" data-testid="upload-options-thumb">
      {src ? <img src={src} alt="" draggable={false} className="h-full w-full object-cover" /> : <FileText className="h-4 w-4 text-slate-400" />}
    </div>
  );
};

const toJpegBytes = (file) => new Promise((res, rej) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const c = document.createElement("canvas");
    c.width = img.naturalWidth; c.height = img.naturalHeight;
    const ctx = c.getContext("2d"); ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height); ctx.drawImage(img, 0, 0);
    URL.revokeObjectURL(url);
    c.toBlob((b) => (b ? b.arrayBuffer().then((ab) => res(new Uint8Array(ab))) : rej(new Error("img"))), "image/jpeg", 0.92);
  };
  img.onerror = () => { URL.revokeObjectURL(url); rej(new Error(`Immagine non leggibile: ${file.name}`)); };
  img.src = url;
});

const mergeToPdf = async (files, name) => {
  const out = await PDFDocument.create();
  for (const f of files) {
    if (isPdf(f)) {
      const src = await PDFDocument.load(await f.arrayBuffer(), { ignoreEncryption: true });
      (await out.copyPages(src, src.getPageIndices())).forEach((p) => out.addPage(p));
    } else {
      const img = f.type === "image/png" ? await out.embedPng(new Uint8Array(await f.arrayBuffer())) : await out.embedJpg(await toJpegBytes(f));
      const page = out.addPage([img.width, img.height]);
      page.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height });
    }
  }
  const bytes = await out.save();
  return new File([bytes], `${baseOf(name.trim()) || "documento-unito"}.pdf`, { type: "application/pdf" });
};

export const UploadOptionsPanel = ({ files, defaultCategoria, categorie, onDirect, onConfirm, onCancel }) => {
  const [list, setList] = useState(() => Array.from(files || []));
  const addInput = useRef(null);
  const addFiles = (fl) => {
    const add = Array.from(fl || []);
    if (!add.length) return;
    const start = list.length;
    setList((l) => [...l, ...add]);
    setNames((n) => [...n, ...add.map((f) => baseOf(f.name))]);
    setOrder((o) => [...o, ...add.map((_, k) => start + k)]);
  };
  const [merge, setMerge] = useState(false);
  const [mergeName, setMergeName] = useState("documento-unito");
  const [names, setNames] = useState(() => list.map((f) => baseOf(f.name)));
  const [cat, setCat] = useState(defaultCategoria);
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(() => list.map((_, i) => i));
  const [dragIdx, setDragIdx] = useState(null);
  const [conv, setConv] = useState({});
  const canConv = (f) => !isPdf(f) && PDF_CONVERTIBLE_RE.test(f.name);
  const move = (from, to) => { if (to < 0 || to >= order.length || from === to) return; setOrder((o) => { const a = [...o]; const [x] = a.splice(from, 1); a.splice(to, 0, x); return a; }); };
  const remove = (fi) => { if (order.length <= 1) { onCancel(); return; } setOrder((o) => o.filter((x) => x !== fi)); };
  const active = order.map((i) => list[i]);
  const mergeable = active.length > 0 && active.every((f) => isPdf(f) || isImg(f));
  const missingConv = !merge && order.some((i) => canConv(list[i]) && conv[i] === undefined);
  const convFlags = (out) => Object.fromEntries(order.map((i, k) => [out[k].name + out[k].size, !!conv[i]]));

  const confirm = async () => {
    setBusy(true);
    try {
      const out = merge
        ? [await mergeToPdf(active, mergeName)]
        : order.map((i) => { const f = list[i]; const nn = (names[i] || "").trim() || baseOf(f.name); return nn === baseOf(f.name) ? f : new File([f], `${nn}${extOf(f.name)}`, { type: f.type, lastModified: f.lastModified }); });
      onConfirm(out, cat, merge ? {} : convFlags(out));
    } catch (e) { toast.error(e.message || "Unione PDF non riuscita"); }
    finally { setBusy(false); }
  };

  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/70 rounded-lg" data-testid="upload-options-panel"
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }} onDrop={(e) => { e.preventDefault(); e.stopPropagation(); addFiles(e.dataTransfer.files); }}>
      <div className="bg-[#121824] border border-slate-700 rounded-lg p-5 w-[560px] max-w-[95%] max-h-[90%] flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-100">Carica {order.length} file</div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => addInput.current?.click()} disabled={busy} data-testid="upload-options-add"
              className="flex items-center gap-1 rounded-md border border-slate-600 px-2 py-1 text-xs font-semibold text-slate-200 hover:bg-slate-800 transition-colors disabled:opacity-50">
              <Plus className="h-3.5 w-3.5" /> Aggiungi file
            </button>
            <input ref={addInput} type="file" multiple className="hidden" data-testid="upload-options-add-input"
              onChange={(e) => { const fl = Array.from(e.target.files || []); e.target.value = ""; addFiles(fl); }} />
            <button type="button" onClick={onCancel} data-testid="upload-options-close" className="text-slate-400 hover:text-slate-100"><X className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="overflow-y-auto space-y-4 pr-1">
          <label className={`flex items-start gap-2 rounded-md border border-slate-700 p-3 ${mergeable ? "cursor-pointer" : "opacity-50"}`}>
            <Checkbox checked={merge} disabled={!mergeable} onCheckedChange={(v) => setMerge(!!v)} data-testid="upload-options-merge" className="mt-0.5" />
            <span>
              <span className="block text-sm font-semibold text-slate-100">1. Unisci tutti in un unico PDF</span>
              <span className="block text-xs text-slate-400">Immagini e PDF vengono fusi in un unico documento. Potrai scegliere un nome unico qui sotto.</span>
              {!mergeable && <span className="block text-[11px] text-amber-400 mt-1">Disponibile solo se tutti i file sono immagini o PDF.</span>}
            </span>
          </label>
          {merge ? (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">2. Nome del PDF unico</div>
              <div className="flex items-center gap-2">
                <Input noUppercase value={mergeName} onChange={(e) => setMergeName(e.target.value)} data-testid="upload-options-merge-name" className="h-9 bg-slate-900/70 border-slate-700 text-slate-100" />
                <NameCaseButtons value={mergeName} onChange={setMergeName} testId="upload-options-merge-case" />
                <span className="text-xs text-slate-400">.pdf</span>
              </div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mt-3 mb-1">Ordine delle pagine</div>
              <div className="text-[11px] text-slate-500 mb-1">Trascina i file (o usa le frecce) per scegliere l'ordine nel PDF.</div>
              <div className="space-y-1" data-testid="upload-options-order">
                {order.map((fi, pos) => (
                  <div key={fi} draggable onDragStart={() => setDragIdx(pos)} onDragEnd={() => setDragIdx(null)}
                    onDragOver={(e) => { e.preventDefault(); if (dragIdx !== null && dragIdx !== pos) { move(dragIdx, pos); setDragIdx(pos); } }}
                    data-testid={`upload-options-order-${pos}`}
                    className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm cursor-grab transition-colors ${dragIdx === pos ? "border-amber-500 bg-amber-500/10" : "border-slate-700 bg-slate-900/50"}`}>
                    <GripVertical className="h-4 w-4 text-slate-500 shrink-0" />
                    <span className="text-xs text-slate-500 w-5">{pos + 1}.</span>
                    <FileThumb file={list[fi]} />
                    <span className="truncate flex-1 text-slate-200">{list[fi].name}</span>
                    <span className="text-[11px] text-slate-500">{fmtSize(list[fi].size)}</span>
                    <button type="button" onClick={() => move(pos, pos - 1)} disabled={pos === 0} data-testid={`upload-options-order-up-${pos}`} className="text-slate-400 hover:text-slate-100 disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
                    <button type="button" onClick={() => move(pos, pos + 1)} disabled={pos === order.length - 1} data-testid={`upload-options-order-down-${pos}`} className="text-slate-400 hover:text-slate-100 disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
                    <button type="button" onClick={() => remove(fi)} title="Rimuovi dalla lista" data-testid={`upload-options-order-remove-${pos}`} className="text-slate-400 hover:text-red-400"><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">2. Rinomina ogni file</div>
              <div className="space-y-2">
                {order.map((i) => { const f = list[i]; return (
                  <div key={`${f.name}-${i}`} data-testid={`upload-options-file-${i}`}>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-slate-500 shrink-0" />
                      <Input noUppercase value={names[i]} onChange={(e) => setNames((a) => a.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`upload-options-name-${i}`} className="h-8 bg-slate-900/70 border-slate-700 text-slate-100 text-sm" />
                      <NameCaseButtons value={names[i]} onChange={(v) => setNames((a) => a.map((x, j) => (j === i ? v : x)))} testId={`upload-options-case-${i}`} />
                      <span className="text-xs text-slate-500">{extOf(f.name)}</span>
                      <button type="button" onClick={() => remove(i)} title="Rimuovi dalla lista" data-testid={`upload-options-remove-${i}`} className="text-slate-400 hover:text-red-400 shrink-0"><X className="h-4 w-4" /></button>
                    </div>
                    <div className="text-[11px] text-slate-500 ml-6 mt-0.5 truncate">{f.name} · {fmtSize(f.size)}</div>
                    {canConv(f) && (
                      <div className="ml-6 mt-1 flex items-center gap-2" data-testid={`upload-options-conv-${i}`}>
                        <span className={`text-xs ${conv[i] === undefined ? "text-amber-400 font-semibold" : "text-slate-300"}`}>Convertire in PDF?</span>
                        <button type="button" onClick={() => setConv((c) => ({ ...c, [i]: true }))} data-testid={`upload-options-conv-yes-${i}`}
                          className={`h-6 px-3 rounded text-[11px] font-semibold border transition-colors ${conv[i] === true ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-600 text-slate-200 hover:bg-slate-800"}`}>Sì</button>
                        <button type="button" onClick={() => setConv((c) => ({ ...c, [i]: false }))} data-testid={`upload-options-conv-no-${i}`}
                          className={`h-6 px-3 rounded text-[11px] font-semibold border transition-colors ${conv[i] === false ? "bg-slate-600 border-slate-600 text-white" : "border-slate-600 text-slate-200 hover:bg-slate-800"}`}>No</button>
                      </div>
                    )}
                    {!isPdf(f) && !canConv(f) && /\.(doc|odt|rtf|xlsx?|pptx?)$/i.test(f.name) && (
                      <div className="ml-6 mt-1 text-[11px] text-slate-500" data-testid={`upload-options-conv-na-${i}`}>Conversione in PDF non disponibile per questo formato: verrà caricato così com'è.</div>
                    )}
                  </div>
                ); })}
              </div>
            </div>
          )}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">3. Categoria allegato</div>
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger className="h-9 border-slate-300 font-medium" style={{ backgroundColor: "#ffffff", color: "#0f172a" }} data-testid="upload-options-categoria"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categorie.map((c) => <SelectItem key={c.k} value={c.k} data-testid={`upload-options-cat-${c.k}`}>{c.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 justify-end border-t border-slate-800 pt-3">
          {missingConv && <span className="text-[11px] text-amber-400 self-center" data-testid="upload-options-conv-missing">Rispondi «Convertire in PDF?» per ogni file</span>}
          <Button size="sm" variant="outline" onClick={() => onDirect(active, merge ? {} : convFlags(active))} disabled={busy || missingConv} data-testid="upload-options-direct" className="border-slate-600 text-slate-200 mr-auto">
            <Zap className="h-4 w-4 mr-1" /> Allega direttamente
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel} disabled={busy} data-testid="upload-options-cancel" className="border-slate-700 text-slate-300">Annulla</Button>
          <Button size="sm" onClick={confirm} disabled={busy || missingConv} data-testid="upload-options-confirm" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />} Carica
          </Button>
        </div>
      </div>
    </div>
  );
};
